import { useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import type {
    CatalogResponse,
    CreateCatalogRequest,
    CreateCatalogResponse,
} from "@models/catalog";
import {
    catalogsAtom,
    isFetchingCatalogsAtom,
    isProcessingCatalogsAtom,
} from "@/store/catalogAtoms";

const CATALOGS_ENDPOINT = "/catalogs";

export const useCatalogApi = () => {
    const { callApi } = useApi();
    // Session-bound: an answer landing after sign-out must not refill it.
    const catalogs = useAtomValue(catalogsAtom);
    const setCatalogs = useSessionSetAtom(catalogsAtom);
    const setIsFetching = useSetAtom(isFetchingCatalogsAtom);
    const setIsProcessing = useSetAtom(isProcessingCatalogsAtom);

    const getCatalogs = useCallback(async (): Promise<CatalogResponse[]> => {
        setIsFetching(true);
        try {
            const data = await callApi<CatalogResponse[]>(CATALOGS_ENDPOINT);
            setCatalogs(data);
            return data;
        } finally {
            setIsFetching(false);
        }
    }, [callApi, setCatalogs, setIsFetching]);

    const getCatalog = useCallback(
        async (catalogId: number): Promise<CatalogResponse> => {
            setIsFetching(true);
            try {
                return await callApi<CatalogResponse>(`${CATALOGS_ENDPOINT}/${catalogId}`);
            } finally {
                setIsFetching(false);
            }
        },
        [callApi, setIsFetching],
    );

    const createCatalog = useCallback(
        async (data: CreateCatalogRequest): Promise<CreateCatalogResponse> => {
            setIsProcessing(true);
            try {
                const response = await callApi<CreateCatalogResponse>(CATALOGS_ENDPOINT, {
                    method: "POST",
                    body: JSON.stringify(data),
                });
                setCatalogs([...catalogs, { id: response.id, name: data.name, description: data.description ?? "" }]);
                return response;
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing, catalogs, setCatalogs],
    );

    const updateCatalog = useCallback(
        async (catalogId: number, data: CreateCatalogRequest): Promise<void> => {
            setIsProcessing(true);
            try {
                await callApi(`${CATALOGS_ENDPOINT}/${catalogId}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    const deleteCatalog = useCallback(
        async (catalogId: number): Promise<void> => {
            setIsProcessing(true);
            try {
                await callApi(`${CATALOGS_ENDPOINT}/${catalogId}`, { method: "DELETE" });
                setCatalogs(catalogs.filter((c) => c.id !== catalogId));
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing, catalogs, setCatalogs],
    );

    return { getCatalogs, getCatalog, createCatalog, updateCatalog, deleteCatalog };
};
